<template>
  <div class="container">
    <main class="content-wrapper">
      <section class="card results-section">
        <div class="card-header">
          <h2>📊 Detection Results</h2>
          <button 
            class="refresh-btn" 
            @click="fetchAnnotatedImages"
            :disabled="isLoading"
          >
            <i class="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
        <div class="card-body">
          <div v-if="errorMessage" class="status-indicator error">
            <i class="fas fa-exclamation-circle"></i>
            <p>{{ errorMessage }}</p>
          </div>
          
          <div v-if="isLoading" class="status-indicator loading">
            <i class="fas fa-circle-notch fa-spin"></i>
            <p>Loading...</p>
          </div>
          
          <div 
            v-if="!isLoading && !errorMessage && Object.keys(sessionImages).length > 0" 
            class="results-gallery"
          >
            <div v-for="(images, sessionId) in sessionImages" :key="sessionId" class="folder-group">
              <h3>📁 Session: {{ sessionId }}</h3>
              <div class="image-grid">
                <div class="image-item" v-for="img in images" :key="img.id">
                  <img 
                    :src="img.src" 
                    :alt="img.original_filename" 
                    class="result-image" 
                    loading="lazy"
                    @error="img.hasError = true"
                    :class="{ 'image-error': img.hasError }"
                  >
                  <p class="image-name">{{ img.original_filename }}</p>
                  <p v-if="img.hasError" class="image-error-text">Load failed</p>
                </div>
              </div>
            </div>
          </div>
          
          <div 
            v-if="!isLoading && !errorMessage && Object.keys(sessionImages).length === 0" 
            class="status-indicator info"
          >
            <i class="fas fa-info-circle"></i>
            <p>No detection results yet</p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const isLoading = ref(true);
const errorMessage = ref('');
const sessionImages = ref({});

const fetchAnnotatedImages = () => {
  isLoading.value = true;
  try {
    const data = localStorage.getItem('sessionImages');
    if (data) {
      sessionImages.value = JSON.parse(data);
    } else {
      sessionImages.value = {};
    }
  } catch (e) {
    errorMessage.value = 'Load failed';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchAnnotatedImages();
});
</script>

<style scoped>
.container {
  max-width: 720px; /* 改窄，紧凑好看 */
  width: 100%;
  margin: 0 auto;
  padding: 12px; /* 整体padding缩小 */
  background-color: #f8fafc;
  box-sizing: border-box;
  /* 去掉多余高度，由内容自然撑开 */
}

.content-wrapper {
  display: block;
}

.card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  background: #f1f5f9;
  padding: 12px 16px; /* 缩小 */
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #1e293b;
  font-weight: 600;
}

.refresh-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.card-body {
  padding: 16px; /* 缩小 */
}

.folder-group {
  margin-bottom: 16px; /* 缩小间距 */
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.folder-group h3 {
  color: #16a34a;
  font-size: 1rem;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); /* 更窄格子 */
  gap: 10px;
}

.image-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.result-image {
  width: 100%;
  height: 130px; /* 图片高度压低，不高耸 */
  object-fit: cover;
  border-radius: 8px;
  background: #f8fafc;
}

.image-name {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #475569;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-error {
  border: 2px dashed #ef4444;
}

.image-error-text {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 4px;
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.9rem;
}

.status-indicator p {
  margin: 0;
}

.loading {
  background: #f0fdf4;
  color: #166534;
}

.error {
  background: #fef2f2;
  color: #dc2626;
}

.info {
  background: #eff6ff;
  color: #3b82f6;
}

/* 手机适配 */
@media (max-width: 768px) {
  .container {
    padding: 8px;
  }
  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .result-image {
    height: 120px;
  }
}
</style>